'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  const fetchProfile = async (userId: string) => {
    try {
      const { data } = await supabase
        .from('user_profiles')
        .select('*, sellers(*)')
        .eq('id', userId)
        .maybeSingle();
      setProfile(data);
    } catch {
      setProfile(null);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) fetchProfile(session.user.id);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // ── Email Magic Link (OTP) — replaces phone OTP for buyers ──────────────────
  const signInWithEmail = async (email: string) => {
    const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : '');
    const { data, error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${siteUrl}/auth/callback`,
        shouldCreateUser: true,
      },
    });
    if (error) throw error;
    return data;
  };

  // Verify email OTP token
  const verifyEmailOtp = async (email: string, token: string) => {
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    });
    if (error) throw error;
    return data;
  };

  // Phone OTP Sign In / Sign Up (kept for seller use)
  const signInWithPhone = async (phone: string) => {
    const formattedPhone = phone.startsWith('+91') ? phone : `+91${phone}`;
    const { data, error } = await supabase.auth.signInWithOtp({
      phone: formattedPhone,
    });
    if (error) throw error;
    return data;
  };

  // Verify Phone OTP
  const verifyOtp = async (phone: string, token: string) => {
    const formattedPhone = phone.startsWith('+91') ? phone : `+91${phone}`;
    const { data, error } = await supabase.auth.verifyOtp({
      phone: formattedPhone,
      token,
      type: 'sms',
    });
    if (error) throw error;
    return data;
  };

  // Register buyer: update profile after OTP verify
  const registerBuyer = async (userId: string, name: string, contact: string) => {
    const isEmail = contact.includes('@');
    const { error } = await supabase
      .from('user_profiles')
      .upsert(
        {
          id: userId,
          full_name: name,
          ...(isEmail ? { email: contact } : { phone: contact }),
          role: 'buyer',
        },
        { onConflict: 'id' }
      );
    if (error) throw error;
    await fetchProfile(userId);
  };

  // Register seller: update profile after OTP verify
  const registerSeller = async (userId: string, name: string, phone: string) => {
    const { error } = await supabase
      .from('user_profiles')
      .upsert({ id: userId, full_name: name, phone, role: 'seller' }, { onConflict: 'id' });
    if (error) throw error;
    await fetchProfile(userId);
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
  };

  // Get Current User
  const getCurrentUser = async () => {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*, sellers(*)')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  // Legacy email/password methods (kept for compatibility)
  const signUp = async (email: string, password: string, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: (metadata as any)?.fullName || '',
          avatar_url: (metadata as any)?.avatarUrl || '',
          role: (metadata as any)?.role || 'buyer',
        },
        emailRedirectTo: `${typeof window !== 'undefined' ? window.location.origin : ''}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const isEmailVerified = () => user?.email_confirmed_at !== null;

  const value = {
    user,
    profile,
    session,
    loading,
    signInWithEmail,
    verifyEmailOtp,
    signInWithPhone,
    verifyOtp,
    registerBuyer,
    registerSeller,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
